import { useRef, useState } from 'react';
import {
  StepCta,
  StepHeading,
  StepSub,
  TertiaryLink,
} from '../../OnboardingShell/StepLayout';
import { useOnboardingV2 } from '../../OnboardingShell/onboardingV2';
import { deriveDisplayName, type DisplayNameUser } from '../../../lib/displayName';
import type { OnboardingAnswers } from '../../../hooks/onboardingReducer';
import { checkOnboardingName } from '../../../hooks/usePersistOnboarding';
import styles from './NameStep.module.css';

/** B472 — what a box of spaces only is told (see `spacesRefused` below). The
 *  character rules ("Use letters, spaces...") do not describe it; this names
 *  the two ways out. No dash, no exclamation mark. */
const BLANK_NAME_SENTENCE = 'Enter a name, or tap Skip for now.';

/**
 * NEW screen 1 — "What should we call you?" (CD onboarding-chapters handoff,
 * founder-ratified 2026-08-30). Opens chapter 1, "Getting to know you".
 *
 * ## Prefill: THE SHARED CHAIN, NOT A FORK
 *
 * The value comes from `lib/displayName`'s `deriveDisplayName` — the ONE
 * fallback chain B73 created precisely to stop this app growing a second copy
 * of it. Two copies is how Today came to greet the founder `GOOD EVENING,
 * RAANROAL`: both copies ended with `email.split('@')[0]`, and an email
 * fragment is not a name. ⛔ Do not inline a `user_metadata.full_name ||
 * user_metadata.name` read here, however small it looks — that IS the fork, and
 * the chain's deliberate STOP (it answers `''`, never the email local part) is
 * the part a fork always loses.
 *
 * `contextData` is passed through so a RESUME shows the athlete's own
 * `preferred_name` rather than the provider's formal name; a fresh athlete has
 * no context, and `deriveDisplayName(user, null)` is then exactly the Apple /
 * Google sign-in name the handoff asks for.
 *
 * ## ⛔ Nothing is written on arrival
 *
 * The prefill is LOCAL state. Merely landing on this screen writes nothing into
 * the wizard — same guarantee `Step2Location` keeps (F5(a): a value the athlete
 * never chose must not be recorded as an answer she gave), and the same reason
 * B73 marked its chain DISPLAY-ONLY: seeding the OAuth name into stored context
 * would race the server's "never overwrite a name the athlete typed" rule.
 * The first write is a keystroke, a Continue, or a Skip.
 *
 * ## The write, and only the write
 *
 * This step hands `displayName` up through `setAnswer`, exactly as every
 * sibling step hands its answer up, and does nothing else. ⛔ No API call, no
 * localStorage write, no touch of the sign-in identity — the completion POST
 * persists, like every other answer in the bag. (B438: it did not, until
 * `buildOnboardingPayload` gained the `preferred_name` projection; the typed
 * name used to reach the reveal headline and nowhere else.)
 *
 * ## Continue is ALWAYS enabled, and an empty submit is a skip
 *
 * Ratified: "CTA 'Continue' — ALWAYS enabled; an empty submit counts as a
 * skip." A minor walks this flow and every new question must be skippable, so
 * the CTA never becomes a wall. (B438: it is still never `disabled`, but a
 * name outside the item 12 rule shows its one sentence and Continue / Enter
 * wait for it; "Skip for now" never waits.) Both skip exits write `displayName: ''`, which is
 * the recorded SKIP: it survives Back-navigation (an `undefined` would
 * re-prefill from sign-in and silently undo her choice) and the persist layer
 * treats it as "write no name", leaving the Profile card empty exactly as
 * today. See the O2 lane report's props contract.
 */
type NameStepProps = {
  answers: OnboardingAnswers;
  setAnswer: (patch: Partial<OnboardingAnswers>) => void;
  onContinue: () => void;
  /**
   * The signed-in user, for the prefill. Structural (`DisplayNameUser`) rather
   * than Supabase's `User` so the step stays trivially testable — the same
   * choice `lib/displayName` made for the same reason.
   */
  authUser?: DisplayNameUser | null;
  /** `athlete_context`, when the route has it. Lets a RESUME show the
   *  athlete's own `preferred_name` ahead of the provider's formal name. */
  contextData?: Record<string, unknown> | null;
};

/** The first word of a full name; `''` for nothing. */
function firstNameOf(full: string): string {
  return full.trim().split(/\s+/)[0] ?? '';
}

export function NameStep({
  answers,
  setAnswer,
  onContinue,
  authUser,
  contextData,
}: NameStepProps) {
  /** What the sign in knows this person is called, or `''`. Read ONCE: a value
   *  that changed mid-step would swap the field under the athlete's cursor.
   *
   *  Harvest fix round (critic MEDIUM 1, UX M6): a name the item 12 rule refuses
   *  (42 characters, a parenthesis, a digit, an emoji) is NOT prefilled. The
   *  screen used to open already refusing the athlete's own name, with Continue
   *  dead before she had typed anything. It starts empty instead, which is the
   *  skip, and the live check still judges whatever she types.
   *
   *  Ruling 5 (2026-10-22): the SIGN-IN name prefills as the FIRST name only.
   *  Continue on the untouched box used to save the provider's full formal name
   *  ("Test Runner"), so the coaches said it in full. A name the athlete typed
   *  earlier (`preferred_name`) is hers and comes back as typed, and whatever
   *  she types here is saved as typed. */
  const [signInName] = useState(() => {
    const preferred =
      typeof contextData?.preferred_name === 'string' ? contextData.preferred_name.trim() : '';
    if (preferred) return checkOnboardingName(preferred).ok ? preferred : '';
    // The sign-in name must pass the rule as a whole before any of it is offered
    // (the harvest rule above); what is offered is then its first word.
    const derived = deriveDisplayName(authUser, contextData);
    if (!checkOnboardingName(derived).ok) return '';
    const first = firstNameOf(derived);
    return checkOnboardingName(first).ok ? first : '';
  });
  // Part 7 (flag `onboarding_2_0_v1`): the helper shares the sentence's one reserved line.
  const v2 = useOnboardingV2();
  const inputRef = useRef<HTMLInputElement>(null);
  /**
   * Held locally so the field is editable before anything is committed, and so
   * the prefill itself is never a write. `answers.displayName` wins when it
   * exists — including the empty string, which is a recorded skip and must not
   * be overwritten by the prefill on a Back-navigation.
   */
  const [value, setValue] = useState<string>(() => answers.displayName ?? signInName);

  /** ⛔ Only true when the sign in actually supplied a name. With no name the
   *  sentence "From your sign in" would be a statement about something that did
   *  not happen, so it is not rendered at all. B472 — and only while the box still
   *  HOLDS that name: once she types her own, the sentence is no longer true of
   *  what is in the box (it stayed on screen under her own name). */
  const showHelper = signInName !== '' && value === signInName;

  /**
   * B438 (rulings 2026-10-21 item 12) — the name rule, checked on every
   * keystroke and again at the exit. `ok` with `''` is the skip. The sentence
   * stands under the field while the box is outside the rule, and the exits
   * below that carry the box's text wait for it. "Skip for now" never waits.
   */
  const check = checkOnboardingName(value);
  /** B464 / M10 — a box of SPACES ONLY is not a skip: an empty box and "Skip for
   *  now" are, a name made of spaces is neither, and used to be accepted in
   *  silence (the box then came back blank under "From your sign in"). It is
   *  refused when Continue or Enter is pressed; it is not flagged while she is
   *  still typing, so a leading space is not an error. B472 — with its own
   *  sentence (`BLANK_NAME_SENTENCE`): the character rules do not describe a box
   *  of spaces, and the sentence names the way out. */
  const [spacesRefused, setSpacesRefused] = useState(false);
  // B460 — ONE rule for every field sentence on every step: it shows once the
  // athlete has left the box (or pressed Continue), then follows the value live.
  // It used to show on the first keystroke here and on blur elsewhere.
  const [touched, setTouched] = useState(false);
  const problem = !touched
    ? null
    : !check.ok
      ? check.error
      : spacesRefused
        ? BLANK_NAME_SENTENCE
        : null;

  /** One exit for both endings. A blank field and the Skip link are the SAME
   *  answer ("no name"), so they take the same path and write the same thing —
   *  there is no second way to skip that could drift from the first. */
  const commit = (raw: string) => {
    setTouched(true);
    if (raw !== '' && raw.trim() === '') {
      setSpacesRefused(true);
      inputRef.current?.focus();
      return;
    }
    const result = checkOnboardingName(raw);
    if (!result.ok) {
      // UX M6 — a refused Continue or Enter leads somewhere: the box takes the
      // focus, so a screen reader hears the field and its sentence.
      inputRef.current?.focus();
      return;
    }
    setAnswer({ displayName: result.value });
    onContinue();
  };

  return (
    <>
      <StepHeading>What should we call you?</StepHeading>
      <StepSub>
        Your coaches use this name when they talk to you. You can change it
        anytime in Settings.
      </StepSub>

      <div className={styles.fields}>
        <label className={styles.label} htmlFor="ob-display-name">
          Your name
        </label>
        <input
          id="ob-display-name"
          ref={inputRef}
          className={styles.input}
          type="text"
          value={value}
          onChange={(e) => {
            setValue(e.target.value);
            setSpacesRefused(false);
            // Handed up on every keystroke, the way sibling steps do, so
            // Back-navigation re-hydrates what she typed. The TRIM happens at
            // commit, not here — trimming mid-word eats the space she is
            // still typing.
            setAnswer({ displayName: e.target.value });
          }}
          onBlur={() => setTouched(true)}
          onKeyDown={(e) => {
            if (e.key !== 'Enter') return;
            e.preventDefault();
            commit(value);
          }}
          autoComplete="name"
          enterKeyHint="done"
          aria-invalid={problem ? true : undefined}
          aria-describedby={problem ? 'ob-display-name-problem' : undefined}
          autoFocus
        />
        {/* B460 — the sentence's room is reserved, so it appearing never moves
            the helper line or the buttons below the box. */}
        <div className={styles.problemSlot}>
          {problem && (
            // The field's own sentence (B438). No dash, no exclamation mark; the
            // red is the text red in the module (4.5:1 on cream and on white).
            <span id="ob-display-name-problem" role="alert" className={styles.fieldProblem}>
              {problem}
            </span>
          )}
          {v2 && !problem && showHelper && (
            <span className={styles.helper}>From your sign in. Edit it if you like.</span>
          )}
        </div>
        {!v2 && showHelper && (
          <span className={styles.helper}>From your sign in. Edit it if you like.</span>
        )}
      </div>

      {/* ⛔ NEVER `disabled`. An empty submit is a skip, not an error. */}
      <StepCta onClick={() => commit(value)}>Continue</StepCta>
      <TertiaryLink onClick={() => commit('')}>Skip for now</TertiaryLink>
    </>
  );
}
